// ISSUES WHOSE SCHEDULE ACTIVITY IS GONE, AND THE ONE SCREEN THAT MOVES
// THEM.
//
// Owner, 2026-10-02: he cleared BA03's schedule on purpose - 615 rows
// deleted - and is rebuilding it. That left 208 of the project's 229
// linked issues pointing at rows nobody can open. Asked whether he
// wanted a selection bar on the issues table or a dedicated view, he
// chose "the orphaned issues view".
//
// GROUPED BY WHERE THEY CAME FROM, which is the whole reason this is a
// view and not a multi-select. A flat list of 208 invites one giant
// move onto a single activity, and that is not what a rebuild means: the
// issues that were on "Level 1 Area A" belong on the NEW "Level 1 Area
// A". The deleted row still carries its name, so each pile can say where
// it came from and be mapped on its own.
//
// ONE GROUP AT A TIME, AND IT SAYS WHAT IT DID. The reply distinguishes
// moved from already-there and names anything skipped, because "done" on
// a 200-row operation is not a report.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Drawer, Select, Button, Empty, Spin, Alert, Tag, Tooltip, message } from "antd";
import { getOrphanedIssues, bulkReassignIssues } from "api/issue/issueApi";
import { url, tokenRef } from "api";
import { withActivityPaths } from "helpers/schedule/activityLabel";
import { leafActivityOptions, LEAF_SELECT_PROPS } from "helpers/schedule/leafActivityOptions";
import { matchOption } from "components/SearchSelect/searchSelect";

const T = { muted: "#6B7280", ink: "#111827", line: "#E5E7EB", warn: "#B45309" };

export default function OrphanedIssuesDrawer({ open, onClose, projectId, onChanged = null }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const [plan, setPlan] = useState([]);
  // The activity chosen per group, and which group is mid-move.
  const [picks, setPicks] = useState({});
  const [busyGroup, setBusyGroup] = useState("");

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const r = await getOrphanedIssues(projectId);
      setData(r);
    } catch (e) {
      // SAY WE COULD NOT ASK. An empty list and a failed request look
      // identical on screen otherwise, and they call for different acts.
      setError(e.message || "Could not load the orphaned issues");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  // The live plan, for the picker. Done and disabled rows take no new
  // work - the server refuses them - so offering them would be the
  // offered-and-refused bug this whole evening has been about.
  useEffect(() => {
    if (!open || !projectId) return;
    let alive = true;
    fetch(`${url}/projects/${projectId}/gantt`, { headers: { Authorization: `Bearer ${tokenRef.current}` } })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((feed) => {
        if (!alive) return;
        // The whole schedule (a finished row is still a parent); only live
        // LEAVES are offered below.
        setPlan(withActivityPaths(feed.items || []));
      })
      .catch(() => { if (alive) setPlan([]); });
    return () => { alive = false; };
  }, [open, projectId]);

  const planOptions = useMemo(
    // THE LOWEST ACTIVITY ONLY, its path beneath (owner, 2026-10-15).
    () => leafActivityOptions(plan, { offer: (it) => it.status !== "done" && !it.disabled }),
    [plan],
  );

  const move = async (group) => {
    const target = picks[group.oldActivityId];
    if (!target) { message.info("Pick the activity to move them to"); return; }
    setBusyGroup(group.oldActivityId);
    try {
      const r = await bulkReassignIssues(group.issues.map((i) => i._id), target, group.oldActivityId);
      // WHAT IT DID, not "done": moved and already-there are different
      // facts, and a re-run that moved nothing is not a failure.
      const bits = [`${r.moved} moved`];
      if (r.alreadyThere) bits.push(`${r.alreadyThere} already there`);
      if ((r.skippedOtherProject || []).length) bits.push(`${r.skippedOtherProject.length} on another project, left alone`);
      message.success(`${r.activity?.name || "Activity"}: ${bits.join(", ")}`, 6);
      await load();
      if (onChanged) onChanged();
    } catch (e) {
      message.error(e.message || "Could not move those issues", 8);
    } finally {
      setBusyGroup("");
    }
  };

  const groups = (data && data.groups) || [];

  return (
    <Drawer
      title="Issues whose schedule activity is gone"
      width={760}
      open={open}
      onClose={onClose}
      destroyOnClose
    >
      {loading ? (
        <div style={{ padding: 40, textAlign: "center" }}><Spin /></div>
      ) : error ? (
        <Alert type="error" showIcon message={error} action={<Button size="small" onClick={load}>Try again</Button>} />
      ) : !groups.length ? (
        <Empty description="Every issue on this project points at an activity that is still on the schedule." />
      ) : (
        <>
          <div style={{ fontSize: 12.5, color: T.muted, marginBottom: 14, lineHeight: 1.5 }}>
            <b style={{ color: T.ink }}>{data.totalIssues.toLocaleString()}</b> issue
            {data.totalIssues === 1 ? "" : "s"} across <b style={{ color: T.ink }}>{data.totalGroups}</b> deleted
            activit{data.totalGroups === 1 ? "y" : "ies"}. Each pile is listed under the activity it came from, so it
            can be moved onto the row that replaced it. Nothing moves until you pick a target and press the button.
          </div>
          {groups.map((g) => (
            <div key={g.oldActivityId || "none"}
                 style={{ border: `1px solid ${T.line}`, borderRadius: 6, padding: 12, marginBottom: 12 }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 8 }}>
                <div style={{ fontWeight: 600, color: T.ink, fontSize: 13 }}>{g.oldActivityName}</div>
                <Tag color={g.oldActivityDeleted ? "orange" : "red"} style={{ marginInlineEnd: 0 }}>
                  {g.oldActivityDeleted ? "deleted from the schedule" : "no longer exists"}
                </Tag>
                <div style={{ marginLeft: "auto", color: T.muted, fontSize: 12 }}>
                  {g.count.toLocaleString()} issue{g.count === 1 ? "" : "s"}
                </div>
              </div>

              {/* THE LISTING IS CAPPED AND SAYS SO. The count above is the
                  real one; without this line a capped list reads as the
                  whole pile. */}
              <div style={{ fontSize: 12, color: T.muted, marginBottom: 8, maxHeight: 96, overflow: "auto" }}>
                {g.issues.map((i) => (
                  <div key={i._id} style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                    {i.displayId ? `#${i.displayId} ` : ""}{i.title || "(no title)"}
                    {i.status ? <span style={{ color: T.muted }}> · {String(i.status).replace(/_/g, " ")}</span> : null}
                  </div>
                ))}
                {g.truncated ? (
                  <div style={{ color: T.warn, marginTop: 4 }}>
                    Showing {g.issues.length} of {g.count.toLocaleString()} — the button moves all {g.count.toLocaleString()}.
                  </div>
                ) : null}
              </div>

              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <Select filterOption={matchOption}
                  style={{ flex: 1, minWidth: 0 }}
                  {...LEAF_SELECT_PROPS}
                  placeholder={planOptions.length ? "Move them to…" : "This project has no live activities yet"}
                  disabled={!planOptions.length}
                  options={planOptions}
                  value={picks[g.oldActivityId] || undefined}
                  onChange={(v) => setPicks((p) => ({ ...p, [g.oldActivityId]: v }))}
                />
                <Tooltip title={!planOptions.length
                  ? "Add the new activities to the schedule first - an issue has to hang off a live row"
                  : `Moves all ${g.count.toLocaleString()} of them`}>
                  <Button type="primary"
                          loading={busyGroup === g.oldActivityId}
                          disabled={!planOptions.length || (!!busyGroup && busyGroup !== g.oldActivityId)}
                          onClick={() => move(g)}>
                    Move {g.count.toLocaleString()}
                  </Button>
                </Tooltip>
              </div>
            </div>
          ))}
        </>
      )}
    </Drawer>
  );
}
